import { Calculation, CalculationListItem, PayrollDetails, PayrollListItem, InvoicePreview, Invoice } from "../../model";
import { OData, ODataResult, ODataQueryOptions } from "../../util";
import { Ajax, CrudApiBase } from "../infra";
import { Configs } from "../../logic/Configs";
import { PayrollLogic } from "../../logic/PayrollLogic";

/**
 * EXPERIMENTAL v4 version of the Payrolls API.
 * NOTE: This is just a quick hack version => Not for production  before complete review!!
 */
export class Payrolls4 extends CrudApiBase<PayrollDetails, PayrollListItem> {

  /**
   * For NG1-dependency injection
   * @ignore
   */
  public static $inject = ["AjaxNg1"];

  /** Base URL for details etc. */
  protected baseUrl = "/v04/api/payroll";

  constructor(ajax: Ajax) {
    super(ajax);
  }

  /**
   * Saves the Payroll object to the storage.
   * If id is null, this is add. If id exists, this is update.
   * @param payroll - The Payroll object to add or update.
   * @param options - Further save options.
   * @param options.applyEmploymentDefaultRows - If true, applies the employment default rows to the calculation when creating a new payroll.
   * @returns The saved Payroll object.
   */
  public save(payroll: PayrollDetails, options: {applyEmploymentDefaultRows: boolean} | undefined = undefined): Promise<PayrollDetails> {
    if (payroll?.info) {
      payroll.info.applicationId = Configs.current?.applicationId;
    }
    return super.save(payroll, options);
  }

  /** Client-side (synchronous) method for getting a new Payroll object as bases for UI binding. */
  public getBlank(): PayrollDetails {
    return PayrollLogic.getBlank();
  }

  /**
   * Adds new calculations to the the Payroll and saves the Payroll object.
   * The calculations are added based on employment relation id's.
   * @param id The Id of the Payroll object to update and save.
   * @param employmentIds Array of employment relation id's for which the a new calculation is added to the Payroll list.
   * @returns Added calculations as list items.
   */
  public addEmployments(id: string, employmentIds: string[]): Promise<CalculationListItem[]> {
    const method = `${this.baseUrl}/${id}/employments`;
    return this.ajax.postJSON(method, employmentIds);
  }


  /**
   * Makes an OData query to the calculations of a single Payroll.
   * @param id The id of the payroll.
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   */
  public getCalcsOData(id: string, query: ODataQueryOptions | string): Promise<ODataResult<CalculationListItem>> {
    const method = `${this.baseUrl}/${id}/calcs`;
    return OData.getOData<CalculationListItem>(method, query, this.ajax);
  }

  /**
   * Adds or updates the calculations to the the Payroll and saves the Payroll object.
   * If calculation is read-only, a copy is made.
   * @param id The id of the Payroll object to update and save.
   * @param calculationIds Array of calculation ids that are added or updated to the Payroll list.
   * @returns Added or updated calculations as list items.
   */
  public addCalculations(id: string, calculationIds: string[]): Promise<CalculationListItem[]> {
    const method = `${this.baseUrl}/${id}/calcs`;
    return this.ajax.postJSON(method, { ids: calculationIds });
  }

 /**
  * Adds or updates the calculations to the the Payroll and saves the Payroll object.
  * If calculation is read-only, a copy is made.
  * @param id The id of the Payroll object to update and save.
  * @param calcsOrIds Array of calculations or calculation ids that are added or updated to the Payroll list.
  * @param applyWorktime If true, resolves, recalculates and updates holidays and absences to the given calculations.
  * @param applyEmploymentDefaultRows If true, adds the employment default rows to the given calculations.
  * @param removeAllRows If true, removes all rows from the calculation before applying employment defaults.
  * @returns Added or updated calculations as list items.
  */
  public saveCalculations(id: string, calcsOrIds: Calculation[], applyWorktime?: boolean, applyEmploymentDefaultRows?: boolean, removeAllRows?: boolean): Promise<CalculationListItem[]>;

 /**
  * Adds or updates the calculations to the the Payroll and saves the Payroll object.
  * If calculation is read-only, a copy is made.
  * @param id The id of the Payroll object to update and save.
  * @param calcsOrIds Array of calculations or calculation ids that are added or updated to the Payroll list.
  * @param applyWorktime If true, resolves, recalculates and updates holidays and absences to the given calculations.
  * @param applyEmploymentDefaultRows If true, adds the employment default rows to the given calculations.
  * @param removeAllRows If true, removes all rows from the calculation before applying employment defaults.
  * @returns Added or updated calculations as list items.
  */
  public saveCalculations(id: string, calcsOrIds: string[], applyWorktime?: boolean, applyEmploymentDefaultRows?: boolean, removeAllRows?: boolean): Promise<CalculationListItem[]>;

 /**
  * Adds or updates the calculations to the the Payroll and saves the Payroll object.
  * If calculation is read-only, a copy is made.
  * @param id The id of the Payroll object to update and save.
  * @param calcsOrIds Array of calculations or calculation ids that are added or updated to the Payroll list.
  * @param applyWorktime If true, resolves, recalculates and updates holidays and absences to the given calculations.
  * @param applyEmploymentDefaultRows If true, adds the employment default rows to the given calculations.
  * @param removeAllRows If true, removes all rows from the calculation before applying employment defaults.
  * @returns Added or updated calculations as list items.
  */
  public saveCalculations(id: string, calcsOrIds: unknown[], applyWorktime = false, applyEmploymentDefaultRows = false, removeAllRows = false): Promise<CalculationListItem[]> {
    const method = `${this.baseUrl}/${id}/calcs`;
    const options = (applyWorktime || applyEmploymentDefaultRows || removeAllRows) ? { applyWorktime, applyEmploymentDefaultRows, "rowMergeOption": (removeAllRows ? "removeAllRows":"") } : {};
    return this.ajax.postJSON(this.getUrlWithOptions(method, options),
    {
      objects: calcsOrIds && calcsOrIds.length && typeof calcsOrIds[0] == "object" ? calcsOrIds : null,
      ids: calcsOrIds && calcsOrIds.length && typeof calcsOrIds[0] == "string" ? calcsOrIds : null
    });
  }

  /**
   * Merges the calculations by employment to the the Payroll and saves the Payroll object.
   * If calculation is read-only, a copy is made.
   * @param id The id of the Payroll object to update and save.
   * @param calculationIds Array of calculation ids that are added or updated to the Payroll list.
   * @param removeAllRows If true, removes all rows from the calculation.
   * @param applyEmploymentDefaultRows If true, applies the employment default rows to the calculation.
   * @returns Added or updated calculations as list items.
   */
   public mergeCalculations(id: string, calculationIds: string[], removeAllRows = false, applyEmploymentDefaultRows = false): Promise<CalculationListItem[]> {
    const method = `${this.baseUrl}/${id}/calcs?isMerge=true&rowMergeOption=${(removeAllRows ? "removeAllRows":"")}&applyEmploymentDefaultRows=${!!applyEmploymentDefaultRows}`;
    return this.ajax.postJSON(method, { ids: calculationIds});
  }

  /**
   * Removes the calculation from the Payroll and saves the Payroll object.
   * The calculation is completely deleted.
   * This can only be done if the calculation and the payroll have not been paid yet.
   * @param id The id of the Payroll  object.
   * @param calculationId The id of the calculation to be removed from the Payroll list and deleted.
   * @returns The Saved Payroll object.
   */
  public deleteCalculation(id: string, calculationId: string): Promise<PayrollDetails> {
    const method = `${this.baseUrl}/${id}/calcs/${calculationId}?delete=true`;
    return this.ajax.remove(method);
  }

  /**
   * Removes the calculation from the Payroll and saves the Payroll object.
   * The calculation is unlinked and it becomes an individual calculation in the draft list (and can then be processed individually).
   * This can only be done if the calculation and the payroll have not been paid yet.
   * @param id The id of the Payroll object.
   * @param calculationId The id of the calculation which is unlinked from the Payroll list.
   * @returns The Saved Payroll object.
   */
  public unlinkCalculation(id: string, calculationId: string): Promise<PayrollDetails> {
    const method = `${this.baseUrl}/${id}/calcs/${calculationId}?delete=false`;
    return this.ajax.remove(method);
  }

  /**
   * Cancels the invoice or payment processing for the given Payroll.
   * Please note that this is possible only if the channel supports the cancel action.
   * @param id The id of the Payroll for which the cancellation is requested.
   * @param calculationIds Array of calculation id's that are cancelled.
   * @param excludePeriodicInvoices If true, excludes the periodic invoice adjust operation.
   * @returns The Saved Payroll object.
   */
  public cancelInvoices(id: string, calculationIds: string[], excludePeriodicInvoices = true): Promise<PayrollDetails> {
    const method = `${this.baseUrl}/${id}/cancel-invoices`;
    const options = { excludePeriodicInvoices };
    return this.ajax.postJSON(this.getUrlWithOptions(method, options), calculationIds);
  }

  /**
   * Returns the invoice preview data for the given Payroll and calculations.
   * @param id The id of the Payroll for which the preview is requested.
   * @param calculationIds Array of calculation id's for which the preview is generated.
   * @param excludePeriodicInvoices If true, excludes the periodic invoices from the preview.
   * @returns Array of invoice preview objects.
   */
  public previewInvoices(id: string, calculationIds: string[], excludePeriodicInvoices = true): Promise<InvoicePreview[]> {
    const method = `${this.baseUrl}/${id}/preview-invoices`;
    const options = { excludePeriodicInvoices };
    return this.ajax.postJSON(this.getUrlWithOptions(method, options), calculationIds);
  }

  /**
   * Creates invoices for the given Payroll and calculations.
   * @param id The id of the Payroll for which the invoices are created.
   * @param calculationIds Array of calculation id's for which the invoices are created.
   * @param excludePeriodicInvoices If true, excludes the periodic invoices from the creation.
   * @returns Array of invoice objects.
   */
  public createInvoices(id: string, calculationIds: string[], excludePeriodicInvoices = true): Promise<Invoice[]> {
    const method = `${this.baseUrl}/${id}/create-invoices`;
    const options = { excludePeriodicInvoices };
    return this.ajax.postJSON(this.getUrlWithOptions(method, options), calculationIds);
  }
}